import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Engine component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconEngine extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.7515 4.83093H14.1905C14.6047 4.83093 14.9405 4.49515 14.9405 4.08093C14.9405 3.66672 14.6047 3.33093 14.1905 3.33093H11.0015H7.81274C7.39853 3.33093 7.06274 3.66672 7.06274 4.08093C7.06274 4.49515 7.39853 4.83093 7.81274 4.83093H10.2515V6.27893H8.12784C7.9191 6.27893 7.7198 6.36593 7.57787 6.519L6.4338 7.7529H4.93895C4.60014 7.7529 4.28781 7.89857 4.06686 8.13687C3.84768 8.37326 3.7334 8.68253 3.7334 8.99422V11.6835H2.5V8.50281C2.5 8.08859 2.16421 7.75281 1.75 7.75281C1.33579 7.75281 1 8.08859 1 8.50281V12.4335V16.364C1 16.7782 1.33579 17.114 1.75 17.114C2.16421 17.114 2.5 16.7782 2.5 16.364V13.1835H3.7334V15.8727C3.7334 16.1844 3.84768 16.4937 4.06686 16.7301C4.28781 16.9684 4.60014 17.1141 4.93895 17.1141H6.30978L7.92067 20.1545C8.05069 20.3999 8.30568 20.5533 8.5834 20.5533H16.1001C16.4389 20.5533 16.7512 20.4077 16.9722 20.1694C17.1913 19.933 17.3056 19.6237 17.3056 19.312V18.0967H19.2223V18.8207C19.2223 19.1324 19.3366 19.4417 19.5557 19.678C19.7767 19.9163 20.089 20.062 20.4278 20.062C21.1291 20.062 21.7891 19.761 22.2663 19.2463C22.7418 18.7335 23.0001 18.0493 23.0001 17.3467V11.4508C23.0001 10.7482 22.7418 10.064 22.2663 9.55123C21.7891 9.03652 21.1291 8.73555 20.4278 8.73555C20.089 8.73555 19.7767 8.88122 19.5557 9.11952C19.3366 9.35591 19.2223 9.66518 19.2223 9.97687V10.7008H17.3056V9.48555C17.3056 9.17385 17.1913 8.86458 16.9722 8.62819C16.7512 8.3899 16.4389 8.24422 16.1001 8.24422H15.1254L13.9825 6.60074C13.8423 6.39914 13.6123 6.27893 13.3667 6.27893H11.7515V4.83093ZM8.45522 7.77893L7.31115 9.01283C7.16922 9.1659 6.96992 9.2529 6.76118 9.2529H5.2334V15.6141H6.76118C7.0389 15.6141 7.29388 15.7675 7.4239 16.0129L9.03479 19.0533H15.8056V17.3467C15.8056 16.9325 16.1414 16.5967 16.5556 16.5967H19.9723C20.3865 16.5967 20.7223 16.9325 20.7223 17.3467V18.515C20.8839 18.4624 21.0371 18.3659 21.1664 18.2265C21.3744 18.0021 21.5001 17.6866 21.5001 17.3467V11.4508C21.5001 11.111 21.3744 10.7954 21.1664 10.5711C21.0371 10.4317 20.8839 10.3352 20.7223 10.2826V11.4508C20.7223 11.8651 20.3865 12.2008 19.9723 12.2008H16.5556C16.1414 12.2008 15.8056 11.8651 15.8056 11.4508V9.74422H14.7334C14.4878 9.74422 14.2578 9.62402 14.1176 9.42242L12.9748 7.77893H8.45522Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-engine') === undefined) {
  customElements.define('cat-icon-engine', BlocksIconEngine);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-engine': BlocksIconEngine;
  }
}
